import axios from "axios";

const state = {
	status: "",
	user: null,
	lastSessionCheck: null, // Track when we last successfully verified the session
	userRateLimitConfig: {
		hasOverride: false,
		isExempt: false,
		customLimitCount: 0,
	},
};

const getters = {
	isLoggedIn(state) {
		return !!state.user;
	},
	authStatus: (state) => state.status,
	userInfo: (state) => {
		return state.user;
	},
	isAdmin(state) {
		if (!state.user || !state.user.userRoles) {
			return false;
		}
		let isAdmin = state.user.userRoles.includes("super_admin") || state.user.userRoles.includes("admin");
		return isAdmin ?? false;
	},
	hasUserRateLimitOverride(state) {
		return state.userRateLimitConfig.hasOverride;
	},
	isUserRateLimitExempt(state) {
		return state.userRateLimitConfig.isExempt;
	},
	userCustomLimitCount(state) {
		return state.userRateLimitConfig.customLimitCount;
	},
};

const mutations = {
	auth_request(state) {
		state.status = "loading";
	},
	auth_success(state, userInfo) {
		state.status = "success";
		state.user = userInfo;
		state.lastSessionCheck = Date.now();
	},
	set_rate_limit_config(state, rateLimitConfig) {
		state.userRateLimitConfig = rateLimitConfig || {
			hasOverride: false,
			isExempt: false,
			customLimitCount: 0,
		};
	},
	auth_error(state) {
		state.status = "error";
	},
	logout(state) {
		state.status = "";
		state.user = null;
		state.lastSessionCheck = null;
		state.userRateLimitConfig = {
			hasOverride: false,
			isExempt: false,
			customLimitCount: 0,
		};
	},
	session_ended(state) {
		state.user = null;
		state.lastSessionCheck = null;
		state.userRateLimitConfig = {
			hasOverride: false,
			isExempt: false,
			customLimitCount: 0,
		};
	},
};

const actions = {
	login({ commit }, user) {
		commit("session_ended");
		return new Promise((resolve, reject) => {
			commit("auth_request");
			axios({ url: "/api/auth/login", data: user, method: "POST" })
				.then((resp) => {
					const userInfo = resp.data.user;
					commit("auth_success", userInfo);

					// Fetch rate limit status after successful login
					axios({ url: "/api/auth/rate-limit-status", method: "GET" })
						.then((rateLimitResp) => {
							commit("set_rate_limit_config", rateLimitResp.data);
						})
						.catch(() => {
							// Silently fail - rate limit status is not critical for login
							commit("set_rate_limit_config", null);
						});

					resolve(resp);
				})
				.catch((err) => {
					commit("auth_error");
					reject(err);
				});
		});
	},
	register({ commit }, user) {
		return new Promise((resolve, reject) => {
			commit("auth_request");
			axios({ url: "/api/users", data: user, method: "POST" })
				.then((resp) => {
					const userInfo = resp.data.user;
					commit("auth_successful register", userInfo);
					resolve(resp);
				})
				.catch((err) => {
					commit("auth_error");
					reject(err);
				});
		});
	},
	logout({ commit }) {
		return new Promise((resolve, reject) => {
			commit("logout");
			axios({ url: "/api/auth/logout", data: {}, method: "POST" })
				.then((resp) => {
					commit("session_ended");
					resolve(resp);
				})
				.catch((err) => {
					commit("auth_error");
					reject(err);
				});
			window.sessionStorage.clear();
			resolve();
		});
	},
	verifySession({ commit, state, getters, rootState }) {
		return new Promise((resolve) => {
			// Check if we already have a valid session before making the request
			const hadExistingSession = getters.isLoggedIn;

			// If we're rate limited and have a recent session check, skip the API call
			const isRateLimited = rootState.rateLimiting?.isRateLimited;
			const sessionAge = state.lastSessionCheck ? Date.now() - state.lastSessionCheck : Infinity;
			const SESSION_CACHE_DURATION = 60000; // 1 minute

			if (hadExistingSession && isRateLimited && sessionAge < SESSION_CACHE_DURATION) {
				// We're rate limited and have a recent session - skip the verification
				console.log("Rate limited with recent session check, skipping verification");
				resolve({ rateLimited: true, maintainSession: true, cached: true });
				return;
			}

			// Only set loading state if we don't have an existing session
			// This prevents clearing the user state unnecessarily
			if (!hadExistingSession) {
				commit("auth_request");
			}

			axios({ url: "/api/auth/session", data: {}, method: "GET" })
				.then((resp) => {
					const userInfo = resp.data;
					commit("auth_success", userInfo);

					// Fetch rate limit status after successful session verification
					axios({ url: "/api/auth/rate-limit-status", method: "GET" })
						.then((rateLimitResp) => {
							commit("set_rate_limit_config", rateLimitResp.data);
						})
						.catch(() => {
							// Silently fail - rate limit status is not critical
							commit("set_rate_limit_config", null);
						});

					resolve(resp);
				})
				.catch((error) => {
					// Check if this is a rate limit error (429)
					if (error.response?.status === 429) {
						// It's a rate limit error - don't end the session
						if (hadExistingSession) {
							// User was previously authenticated, maintain that state
							// Don't call session_ended, just maintain current state
							console.log("Rate limited on session verification, maintaining existing session");
							// Reset status back to success since we're keeping the session
							if (state.user) {
								commit("auth_success", state.user);
							}
							resolve({ rateLimited: true, maintainSession: true });
						} else {
							// No existing session to maintain
							commit("session_ended");
							resolve();
						}
					} else {
						// Any other error - treat as session ended
						commit("session_ended");
						resolve();
					}
				});
		});
	},
};

export default {
	state,
	mutations,
	actions,
	getters,
};
